import React, { useMemo } from 'react'
import styled from 'styled-components'
import { generateMapboxStaticImageUrl } from '../../../utils/mapbox'

interface EstimateMapImageProps {
  lat?: number
  lon?: number
  geo_polygon?: {
    type: 'Polygon'
    coordinates: number[][][]
  }
  width?: number
  height?: number
  zoom?: number
  className?: string
  alt?: string
}

const ImageContainer = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const PlaceholderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.background.secondary};
  color: ${({ theme }) => theme.colors.text.tertiary};
`

/**
 * EstimateMapImage Component
 *
 * Generates a Mapbox Static API image for an estimate property.
 * If a polygon is provided, it will be overlaid on the satellite image.
 * Falls back to a simple center point if no polygon is available.
 */
export const EstimateMapImage: React.FC<EstimateMapImageProps> = ({
  lat,
  lon,
  geo_polygon,
  width = 300,
  height = 169,
  zoom = 17,
  className,
  alt = 'Property location',
}) => {
  const imageUrl = useMemo(() => {
    if (!lat || !lon) {
      return ''
    }

    return generateMapboxStaticImageUrl({
      lat,
      lon,
      width,
      height,
      zoom,
      geoPolygon: geo_polygon,
    })
  }, [lat, lon, geo_polygon, width, height, zoom])

  if (!imageUrl) {
    return (
      <PlaceholderContainer className={className}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22S19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z"
            fill="currentColor"
            opacity="0.4"
          />
        </svg>
      </PlaceholderContainer>
    )
  }

  return (
    <ImageContainer
      src={imageUrl}
      alt={alt}
      className={className}
      loading="lazy"
    />
  )
}
